import { getCookieOptions, getCookies } from "@biggeo/bg-common";
import {
    Applications,
    AuthenticatedUser,
    Organization,
    User,
} from "@biggeo/bg-server-lib/studio";
import {
    Failure,
    Initialized,
    Pending,
    RemoteData,
    Success,
    isFailure,
    isSuccess,
    map,
} from "@vividtheory/remotedata";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import omitBy from "lodash/omitBy";
import LogRocket from "logrocket";
import { match } from "ts-pattern";
import { ActionType, action } from "typesafe-actions";
import Cookies from "universal-cookie";
import { getDomain, logRocketIDFromEnv } from "../../config/env.config";

export const userRootStateKey = "user";

interface IModel {
    readonly userRemoteData: RemoteData<string, AuthenticatedUser>;
    readonly userUuid?: string;
    readonly userOrganizationsRemoteData: RemoteData<
        string,
        readonly Organization[]
    >;
}

const initialState: IModel = {
    userRemoteData: new Initialized(),
    userUuid: undefined,
    userOrganizationsRemoteData: new Initialized(),
};

export const userActions = {
    setUserRemoteData: (v: RemoteData<string, AuthenticatedUser>) =>
        action("SET_USER_REMOTE_DATA", v),
    updateUser: (user: Partial<User>) =>
        action("UPDATE_USER_REMOTE_DATA", user),
    onAuthenticationValidation: (i: {
        v?: RemoteData<string, AuthenticatedUser>;
        type: "failure" | "success" | "request";
    }) => action("ON_AUTHENTICATION_VALIDATION", i),
    remove: () => action("REMOVE"),
    updateOrganization: (org: Organization) =>
        action("UPDATE_ORG_REMOTE_DATA", org),
    setUserOrganizationsRemoteData: (
        orgs: RemoteData<string, readonly Organization[]>
    ) => action("SET_USER_ORGANIZATIONS_REMOTE_DATA", orgs),
};
const domain = getDomain();
const cookies = new Cookies();
export const usersReducer = (
    // biome-ignore lint/style/useDefaultParameterLast: <explanation>
    state: IModel = initialState,
    action: ActionType<typeof userActions>
): IModel => {
    if (!action) return state;

    const appCookies = getCookies(Applications.datascape);

    switch (action.type) {
        case "UPDATE_USER_REMOTE_DATA":
            return {
                ...state,
                userRemoteData: map(
                    (user) => ({
                        ...user,
                        user: {
                            ...user.user,
                            ...omitBy(action.payload, isUndefined || isNull),
                        },
                    }),
                    state.userRemoteData
                ),
            };

        case "UPDATE_ORG_REMOTE_DATA":
            return {
                ...state,
                userRemoteData: map(
                    (user) => ({
                        ...user,
                        organization: {
                            ...user.organization,
                            ...action.payload,
                        },
                    }),
                    state.userRemoteData
                ),
                userOrganizationsRemoteData: map(
                    (orgs) =>
                        orgs.map((org) =>
                            org.id === action.payload.id ? action.payload : org
                        ),
                    state.userOrganizationsRemoteData
                ),
            };

        case "SET_USER_ORGANIZATIONS_REMOTE_DATA":
            return {
                ...state,
                userOrganizationsRemoteData: action.payload,
            };

        case "SET_USER_REMOTE_DATA": {
            if (isSuccess(action.payload)) {
                cookies.set(
                    appCookies.jwt,
                    action.payload.data.jwt,
                    getCookieOptions(domain)
                );
                const DATASCAPE_LOG_ROCKET_ID = logRocketIDFromEnv();
                if (DATASCAPE_LOG_ROCKET_ID) {
                    const { id, firstName, lastName, email } =
                        action.payload.data.user;
                    LogRocket.identify(id, {
                        name: `${firstName} ${lastName}`,
                        email: email,
                    });
                }

                return {
                    ...state,
                    userRemoteData: new Success(action.payload.data),
                };
            }
            return {
                ...state,
                userRemoteData: action.payload,
            };
        }

        case "ON_AUTHENTICATION_VALIDATION": {
            return {
                ...state,
                userRemoteData: match(action.payload.type)
                    .with("request", () => new Pending())
                    .with("success", () => {
                        if (action.payload.v && isSuccess(action.payload.v)) {
                            cookies.set(
                                appCookies.jwt,
                                action.payload.v.data.jwt,
                                getCookieOptions(domain)
                            );

                            return new Success(action.payload.v.data);
                        }
                        return new Initialized();
                    })
                    .with("failure", () => {
                        if (action.payload.v && isFailure(action.payload.v)) {
                            cookies.remove(
                                appCookies.jwt,
                                getCookieOptions(domain)
                            );

                            return new Failure(action.payload.v.error);
                        }
                        return new Initialized();
                    })
                    .exhaustive(),
            };
        }

        case "REMOVE":
            cookies.remove(appCookies.jwt, getCookieOptions(domain));
            return { ...initialState };

        default:
            return state;
    }
};
